/**
 * PaymentConfirmationForm — PUBLIC page (no auth required).
 *
 * The URL that the customer receives via WhatsApp after accepting a proposal.
 * They submit their UTR + screenshot here; the Accounts team confirms.
 *
 * Route: /payment-confirmation/:id?token=...   (outside ProtectedRoute)
 * Squad E — Apr 2026
 */

import { useState, useRef, useCallback } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { UploadCloud, CheckCircle, AlertCircle, X, Camera } from 'lucide-react';

const API_URL = ((import.meta as any).env?.VITE_API_URL as string | undefined) || '/api/v1';

function compressToBase64(file: File, maxWidth = 1200): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const src = e.target?.result as string;
      const img = new Image();
      img.onload = () => {
        const canvas = document.createElement('canvas');
        let { width, height } = img;
        if (width > maxWidth) {
          height = Math.round((height * maxWidth) / width);
          width = maxWidth;
        }
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext('2d')!;
        ctx.drawImage(img, 0, 0, width, height);
        resolve(canvas.toDataURL('image/jpeg', 0.78));
      };
      img.onerror = reject;
      img.src = src;
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

function formatFileSize(bytes: number) {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

type Stage = 'form' | 'submitting' | 'done' | 'error';

export function PaymentConfirmationForm() {
  const { id } = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token') || '';

  const [stage, setStage] = useState<Stage>('form');
  const [utr, setUtr] = useState('');
  const [notes, setNotes] = useState('');
  const [amountClaimed, setAmountClaimed] = useState('');
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [errorMsg, setErrorMsg] = useState('');
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFileChange = useCallback(async (file: File) => {
    if (!file.type.startsWith('image/')) {
      setErrorMsg('Please upload an image file (JPG, PNG, etc.)');
      return;
    }
    if (file.size > 15 * 1024 * 1024) {
      setErrorMsg('File is too large. Please upload an image under 15 MB.');
      return;
    }
    setErrorMsg('');
    setImageFile(file);
    // Show preview
    const preview = URL.createObjectURL(file);
    setImagePreview(preview);
  }, []);

  const handleDrop = useCallback(
    (e: React.DragEvent<HTMLDivElement>) => {
      e.preventDefault();
      const file = e.dataTransfer.files?.[0];
      if (file) handleFileChange(file);
    },
    [handleFileChange],
  );

  const removeImage = () => {
    setImageFile(null);
    setImagePreview(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrorMsg('');

    if (!utr.trim() && !imageFile) {
      setErrorMsg('Please provide your UTR number or upload a payment screenshot.');
      return;
    }

    if (!token) {
      setErrorMsg('This link is invalid or has expired. Please contact the sales team.');
      return;
    }

    setStage('submitting');

    try {
      let screenshotBase64: string | undefined;
      if (imageFile) {
        screenshotBase64 = await compressToBase64(imageFile);
        // Strip the data: prefix — backend adds it back
        screenshotBase64 = screenshotBase64.replace(/^data:[^;]+;base64,/, '');
      }

      const body: any = { token };
      if (utr.trim()) body.utr = utr.trim();
      if (notes.trim()) body.notes = notes.trim();
      if (amountClaimed.trim()) body.amountClaimed = amountClaimed.trim();
      if (screenshotBase64) body.screenshotBase64 = screenshotBase64;

      const res = await fetch(`${API_URL}/payment-confirmations`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });

      const json = await res.json();

      if (!res.ok || !json.success) {
        throw new Error(json.error || 'Submission failed. Please try again.');
      }

      setStage('done');
    } catch (err: any) {
      setErrorMsg(err.message || 'Failed to submit. Please check your connection and try again.');
      setStage('form');
    }
  };

  // ── Done state ────────────────────────────────────────────────────────────
  if (stage === 'done') {
    return (
      <div className="flex min-h-screen items-center justify-center bg-[#faf8f4] p-4">
        <div className="w-full max-w-md rounded-3xl bg-white p-8 shadow-lg text-center">
          <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-emerald-50">
            <CheckCircle className="h-8 w-8 text-emerald-600" />
          </div>
          <h2 className="mb-2 font-display text-2xl font-bold text-gray-900">Details Received!</h2>
          <p className="text-gray-600 leading-relaxed">
            Your payment details have been submitted successfully.
            <br />
            The team will verify and confirm your order shortly.
          </p>
          <p className="mt-4 rounded-xl bg-amber-50 p-3 text-sm text-amber-800">
            You will receive a WhatsApp confirmation once your payment is verified.
          </p>
        </div>
      </div>
    );
  }

  // ── Form state ────────────────────────────────────────────────────────────
  return (
    <div className="flex min-h-screen items-center justify-center bg-[#faf8f4] p-4">
      <div className="w-full max-w-md">
        {/* Header */}
        <div className="mb-6 text-center">
          <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-2xl bg-[#f5870a] text-white text-2xl font-bold shadow">
            A
          </div>
          <h1 className="font-display text-2xl font-bold text-gray-900">Confirm Payment</h1>
          <p className="mt-1 text-sm text-gray-500">
            Share your UTR number or upload a screenshot to confirm your order.
          </p>
        </div>

        <form onSubmit={handleSubmit} className="rounded-3xl bg-white p-6 shadow-lg space-y-5">

          {/* UTR */}
          <div>
            <label className="mb-1 block text-sm font-semibold text-gray-700">
              UTR / Transaction ID <span className="font-normal text-gray-400">(recommended)</span>
            </label>
            <input
              type="text"
              value={utr}
              onChange={(e) => setUtr(e.target.value)}
              placeholder="e.g. 123456789012"
              className="w-full rounded-xl border border-gray-200 bg-gray-50 px-4 py-2.5 font-mono text-sm text-gray-900 placeholder-gray-400 focus:border-[#f5870a] focus:bg-white focus:outline-none focus:ring-2 focus:ring-[#f5870a]/20"
            />
            <p className="mt-1 text-xs text-gray-400">
              12–22 digit code shown in your UPI app / bank app after payment
            </p>
          </div>

          {/* Amount */}
          <div>
            <label className="mb-1 block text-sm font-semibold text-gray-700">
              Amount Paid <span className="font-normal text-gray-400">(₹)</span>
            </label>
            <input
              type="number"
              value={amountClaimed}
              onChange={(e) => setAmountClaimed(e.target.value)}
              placeholder="e.g. 45000"
              min={0}
              step="0.01"
              className="w-full rounded-xl border border-gray-200 bg-gray-50 px-4 py-2.5 text-sm text-gray-900 placeholder-gray-400 focus:border-[#f5870a] focus:bg-white focus:outline-none focus:ring-2 focus:ring-[#f5870a]/20"
            />
          </div>

          {/* Screenshot upload */}
          <div>
            <label className="mb-1 block text-sm font-semibold text-gray-700">
              Payment Screenshot <span className="font-normal text-gray-400">(optional but recommended)</span>
            </label>

            {imagePreview ? (
              <div className="relative rounded-xl border border-gray-200 bg-gray-50 p-3">
                <img
                  src={imagePreview}
                  alt="Payment screenshot preview"
                  className="mx-auto max-h-48 rounded-lg object-contain"
                />
                <div className="mt-2 flex items-center justify-between">
                  <p className="text-xs text-gray-500">{imageFile?.name} — {formatFileSize(imageFile?.size || 0)}</p>
                  <button
                    type="button"
                    onClick={removeImage}
                    className="rounded-full p-1 hover:bg-gray-100"
                  >
                    <X className="h-4 w-4 text-gray-500" />
                  </button>
                </div>
              </div>
            ) : (
              <div
                onDrop={handleDrop}
                onDragOver={(e) => e.preventDefault()}
                className="flex cursor-pointer flex-col items-center justify-center rounded-xl border-2 border-dashed border-gray-200 bg-gray-50 py-8 transition-colors hover:border-[#f5870a] hover:bg-orange-50"
                onClick={() => fileInputRef.current?.click()}
              >
                <div className="flex gap-3 text-gray-400">
                  <UploadCloud className="h-6 w-6" />
                  <Camera className="h-6 w-6" />
                </div>
                <p className="mt-2 text-sm font-medium text-gray-600">Tap to upload screenshot</p>
                <p className="text-xs text-gray-400">JPG, PNG, WEBP — max 15 MB</p>
              </div>
            )}

            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              capture="environment"
              className="hidden"
              onChange={(e) => {
                const f = e.target.files?.[0];
                if (f) handleFileChange(f);
              }}
            />
          </div>

          {/* Notes */}
          <div>
            <label className="mb-1 block text-sm font-semibold text-gray-700">
              Additional Notes <span className="font-normal text-gray-400">(optional)</span>
            </label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={2}
              placeholder="e.g. Paid via HDFC NEFT on 30 Apr..."
              className="w-full rounded-xl border border-gray-200 bg-gray-50 px-4 py-2.5 text-sm text-gray-900 placeholder-gray-400 focus:border-[#f5870a] focus:bg-white focus:outline-none focus:ring-2 focus:ring-[#f5870a]/20"
            />
          </div>

          {/* Error */}
          {errorMsg && (
            <div className="flex items-start gap-2 rounded-xl bg-red-50 p-3 text-sm text-red-700">
              <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
              {errorMsg}
            </div>
          )}

          {/* Submit */}
          <button
            type="submit"
            disabled={stage === 'submitting'}
            className="w-full rounded-xl bg-[#f5870a] px-4 py-3 text-sm font-bold text-white shadow hover:bg-[#e07809] disabled:opacity-60 transition-colors"
          >
            {stage === 'submitting' ? (
              <span className="flex items-center justify-center gap-2">
                <svg className="h-4 w-4 animate-spin" viewBox="0 0 24 24" fill="none">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.4 0 0 5.4 0 12h4z" />
                </svg>
                Submitting...
              </span>
            ) : (
              'Submit Payment Proof'
            )}
          </button>
        </form>

        <p className="mt-4 text-center text-xs text-gray-400">
          Powered by AURA · Ardhanex Tech
        </p>
      </div>
    </div>
  );
}

export default PaymentConfirmationForm;
